/**
 * LogoutPage — dedicated /logout route that wipes ALL local data and reloads.
 *
 * Matches v1 behavior:
 *   1. localStorage.clear() immediately
 *   2. Close db singleton → Dexie.delete() (safe even if DB doesn't exist)
 *   3. Shows progress: "Logging out…" → "Deleting database…" → "Cleaning up…"
 *   4. Reload → fresh state → login UI
 *
 * Rendered OUTSIDE the DatabaseProvider, so Dexie Cloud interactions
 * don't interfere with the logout flow.
 *
 * NEVER-01: Only reached when user explicitly confirms data deletion
 * (via AccountLogout, AccountDelete, or remote force_logout).
 */

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Dexie from "dexie"
import { DEXIE_DB_NAME } from "@/config/environment"
import { db } from "@/db/schema"
import { IndeterminateBar } from "@/app/components/ui/IndeterminateBar"

export function LogoutPage() {
  const { t } = useTranslation()
  const [message, setMessage] = useState(t("Logging out…"))

  useEffect(() => {
    // 1. Clear all browser storage immediately
    localStorage.clear()
    sessionStorage.clear()

    const performLogout = async () => {
      try {
        // Close the module-level db singleton first. Dexie.exists() (which is
        // what we used to call) opens and immediately closes a connection, but
        // IDBDatabase.close() is fire-and-forget at the browser level — Chrome
        // can still see the connection as "closing" when deleteDatabase() is
        // called, causing it to block indefinitely. Closing db explicitly here
        // ensures all connections are released before we delete.
        try {
          db.close()
        } catch {
          // db may not be open — ignore
        }
        setMessage(t("Deleting database…"))
        // deleteDatabase() succeeds even if the DB doesn't exist, so no
        // need to check with Dexie.exists() first.
        await Dexie.delete(DEXIE_DB_NAME)
        setMessage(t("Cleaning up…"))
      } catch (error) {
        console.error("[LogoutPage] Logout failed:", error)
        setMessage(t("Error during logout. Reloading…"))
      }

      // Small delay so user sees the final message, then reload (like v1)
      setTimeout(() => {
        window.location.href = "/"
      }, 1500)
    }

    performLogout()
  }, [])

  return (
    <div className="flex min-h-screen items-center justify-center bg-foreground/5">
      <div className="flex flex-col items-center gap-4">
        <IndeterminateBar className="w-48" />
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  )
}
